// Inbox: the caller's conversations. DIRECT conversations don't carry a title;
// until directory name-resolution lands (follow-up) we label them from what we have.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'lucide-react';
import type { Availability, ConversationSummary } from '../types';
import { directCounterpart } from '../directCounterpart';
import { rowActionFor, type RowAction } from '../rowActions';
import { PersonAvatar } from './PersonAvatar';
import { AvailabilityDotOverlay } from './AvailabilityBadge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** How a row is titled — shared with the page so a confirmation names the same thing
 *  the person just clicked on. */
export function conversationLabel(c: ConversationSummary, selfId?: string, nameOf?: (id?: string) => string): string {
  if (c.title) return c.title;
  if (c.type === 'DIRECT') {
    const other = directCounterpart(c.participant_user_ids, selfId);
    return (nameOf?.(other) || (other ? other.slice(0, 8) : '')) || 'Direct message';
  }
  if (c.type === 'GROUP') return 'Circle';
  return c.type.charAt(0) + c.type.slice(1).toLowerCase();
}

interface Props {
  inbox: ConversationSummary[];
  loading: boolean;
  activeId: string | null;
  selfId?: string;
  nameOf?: (id?: string) => string;
  avatarOf?: (id?: string) => string | undefined;
  /** DIRECT rows carry the other person's status dot (courtesy signal, not a dashboard). */
  availabilityOf?: (id?: string) => Availability | undefined;
  onSelect: (id: string) => void;
  onToggleFavorite?: (id: string) => void;
  /** "Get this off my list" — Remove / Leave / Archive, chosen by the server
   *  (`list_action`) with the local rule as fallback. See rowActions.ts. */
  onRowAction?: (conversation: ConversationSummary, action: RowAction) => void;
  emptyLabel?: string;
}

export const InboxList: React.FC<Props> = ({ inbox, loading, activeId, selfId, nameOf, avatarOf, availabilityOf, onSelect, onToggleFavorite, onRowAction, emptyLabel }) => {
  const { t } = useTranslation('conversations');
  const actionLabel: Record<RowAction, string> = {
    remove: t('row_actions.remove', { defaultValue: 'Remove' }),
    leave: t('row_actions.leave', { defaultValue: 'Leave' }),
    archive: t('row_actions.archive', { defaultValue: 'Archive' }),
  };
  if (loading && inbox.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground">Loading conversations…</div>;
  }
  if (inbox.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {emptyLabel ?? 'No conversations yet. Start one to begin messaging.'}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {inbox.map((c) => {
        const active = c.id === activeId;
        const title = conversationLabel(c, selfId, nameOf);
        // A direct conversation is one person → show their profile picture. Groups/
        // spaces have no single face, so they fall back to initials.
        const otherId = c.type === 'DIRECT' ? directCounterpart(c.participant_user_ids, selfId) : undefined;
        const avatarUrl = otherId ? avatarOf?.(otherId) : undefined;
        const availability = otherId ? availabilityOf?.(otherId) : undefined;
        return (
          <li
            key={c.id}
            className={`group flex items-center transition-colors ${
              active ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-muted/50'
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(c.id)}
              className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3 text-left"
            >
              <AvailabilityDotOverlay availability={availability}>
                <PersonAvatar name={title} avatarUrl={avatarUrl} size={40} />
              </AvailabilityDotOverlay>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-charcoal">{title}</span>
                  <span className="flex-none text-xs text-muted-foreground">{relativeTime(c.last_message_at)}</span>
                </span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {c.type === 'DIRECT' ? 'Direct message' : `${c.participant_user_ids.length} members`}
                </span>
              </span>
            </button>
            <div className="flex flex-none items-center gap-1 pl-1 pr-2">
              {c.unread_hint > 0 && (
                <span className="rounded-full bg-primary-500 px-2 py-0.5 text-xs font-semibold text-white">
                  {c.unread_hint > 99 ? '99+' : c.unread_hint}
                </span>
              )}
              {onToggleFavorite && (
                <button
                  type="button"
                  onClick={() => onToggleFavorite(c.id)}
                  aria-label={c.is_favorite ? 'Unstar' : 'Star'}
                  aria-pressed={!!c.is_favorite}
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-base transition-opacity hover:bg-muted ${
                    c.is_favorite ? 'text-amber-400 opacity-100' : 'text-muted-foreground opacity-0 focus:opacity-100 group-hover:opacity-100'
                  }`}
                >
                  {c.is_favorite ? '★' : '☆'}
                </button>
              )}
              {onRowAction && (() => {
                const action = rowActionFor(c);
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid={`inbox-row-menu-${c.id}`}
                        aria-label={t('row_actions.more', { defaultValue: 'More actions for {{name}}', name: title })}
                        className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-muted-foreground transition-opacity hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 opacity-100 md:opacity-0 md:focus:opacity-100 md:group-hover:opacity-100 md:data-[state=open]:opacity-100"
                      >
                        <MoreVertical className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[10rem]">
                      <DropdownMenuItem
                        data-testid={`inbox-row-action-${c.id}`}
                        data-action={action}
                        className={action === 'remove' ? 'cursor-pointer' : 'cursor-pointer text-red-600 focus:text-red-600'}
                        onSelect={() => onRowAction(c, action)}
                      >
                        {actionLabel[action]}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}
            </div>
          </li>
        );
      })}
    </ul>
  );
};
